import { useMutation } from '@tanstack/react-query';
import { ImageUp, Save } from 'lucide-react';
import { useMemo, useState } from 'react';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import type { SurveyVisitDetail } from '../../types/domain';
import { QuestionInput } from './ManualSurveyDialog';

function pakistanDateTimeLocal(value: string) {
  return new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    timeZone: 'Asia/Karachi',
  }).format(new Date(value)).replace(' ', 'T');
}

export function ManualSurveyEditForm({
  accessToken,
  visit,
  workspaceId,
  onCancel,
  onSaved,
}: {
  accessToken: string;
  visit: SurveyVisitDetail;
  workspaceId: string;
  onCancel: () => void;
  onSaved: (visit: SurveyVisitDetail) => void;
}) {
  const [outlet, setOutlet] = useState({
    name: visit.outlet_name_snapshot,
    city: visit.outlet_city_snapshot,
    area: visit.outlet_area_snapshot,
    address: visit.outlet_address_snapshot,
    ownerName: visit.outlet_owner_name,
    ownerContact: visit.outlet_owner_contact,
  });
  const [capturedAt, setCapturedAt] = useState(() => pakistanDateTimeLocal(visit.captured_at));
  const [latitude, setLatitude] = useState(visit.latitude ?? '');
  const [longitude, setLongitude] = useState(visit.longitude ?? '');
  const [accuracy, setAccuracy] = useState(visit.horizontal_accuracy_m ?? '');
  const [photo, setPhoto] = useState<File | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>(() => Object.fromEntries(
    visit.answers.map((answer) => [answer.question_id, answer.value]),
  ));
  const questions = useMemo(
    () => visit.questions.filter((question) => !['photo', 'location'].includes(question.kind)),
    [visit.questions],
  );
  const visibleQuestions = questions.filter((question) => {
    const condition = question.visibility_condition as { question?: string; equals?: unknown };
    if (!condition.question) return true;
    const controlling = questions.find((item) => item.key === condition.question);
    return controlling ? answers[controlling.id] === condition.equals : false;
  });

  const mutation = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.set('outlet_name', outlet.name);
      form.set('outlet_city', outlet.city);
      form.set('outlet_area', outlet.area);
      form.set('outlet_address', outlet.address);
      form.set('outlet_owner_name', outlet.ownerName);
      form.set('outlet_owner_contact', outlet.ownerContact);
      form.set('captured_at', capturedAt);
      if (latitude.trim()) form.set('latitude', latitude.trim());
      if (longitude.trim()) form.set('longitude', longitude.trim());
      if (accuracy.trim()) form.set('horizontal_accuracy_m', accuracy.trim());
      form.set('answers', JSON.stringify(visibleQuestions.flatMap((question) => {
        const value = answers[question.id];
        if (value === undefined || value === '' || (Array.isArray(value) && !value.length)) return [];
        return [{ question_id: question.id, value }];
      })));
      if (photo) form.set('storefront_photo', photo);
      const params = workspaceId ? `?${new URLSearchParams({ workspace: workspaceId }).toString()}` : '';
      return apiRequest<SurveyVisitDetail>(`/surveys/review/${visit.id}/${params}`, {
        accessToken,
        method: 'PATCH',
        body: form,
      });
    },
    onSuccess: onSaved,
  });

  const setOutletValue = (key: keyof typeof outlet, value: string) => {
    setOutlet((current) => ({ ...current, [key]: value }));
  };

  return <form className="management-form manual-survey-edit" onSubmit={(event) => { event.preventDefault(); mutation.mutate(); }}>
    <section className="form-grid">
      <label>Shop name<input onChange={(event) => setOutletValue('name', event.target.value)} required value={outlet.name} /></label>
      <label>Paper collection time<input onChange={(event) => setCapturedAt(event.target.value)} required type="datetime-local" value={capturedAt} /></label>
      <label>City<input onChange={(event) => setOutletValue('city', event.target.value)} required value={outlet.city} /></label>
      <label>Area<input onChange={(event) => setOutletValue('area', event.target.value)} value={outlet.area} /></label>
      <label>Address<input onChange={(event) => setOutletValue('address', event.target.value)} value={outlet.address} /></label>
      <label>Owner name<input onChange={(event) => setOutletValue('ownerName', event.target.value)} value={outlet.ownerName} /></label>
      <label>Owner contact<input onChange={(event) => setOutletValue('ownerContact', event.target.value)} value={outlet.ownerContact} /></label>
      <label>Latitude<input inputMode="decimal" onChange={(event) => setLatitude(event.target.value)} placeholder="Optional" step="any" type="number" value={latitude} /></label>
      <label>Longitude<input inputMode="decimal" onChange={(event) => setLongitude(event.target.value)} placeholder="Optional" step="any" type="number" value={longitude} /></label>
      <label>GPS accuracy (metres)<input inputMode="decimal" min="0" onChange={(event) => setAccuracy(event.target.value)} placeholder="Optional" step="any" type="number" value={accuracy} /></label>
    </section>
    <label className="manual-survey-photo-upload"><span>Shop picture</span><span className="manual-survey-file-control"><ImageUp size={18} /><input accept="image/jpeg,image/png,image/webp" onChange={(event) => setPhoto(event.target.files?.[0] ?? null)} type="file" /></span><small>{photo ? photo.name : visit.storefront_photo ? 'Choose a file only to replace the current picture.' : 'Upload an optional shop picture from this computer.'}</small></label>
    <section className="manual-survey-questions"><h3>Survey answers</h3>{visibleQuestions.map((question) => <label key={question.id}><span>{question.label}{question.required ? ' *' : ''}</span><QuestionInput onChange={(value) => setAnswers((current) => ({ ...current, [question.id]: value }))} question={question} value={answers[question.id]} />{question.help_text ? <small>{question.help_text}</small> : null}</label>)}</section>
    {mutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(mutation.error)}</p> : null}
    <div className="confirmation-actions"><button className="button secondary" disabled={mutation.isPending} onClick={onCancel} type="button">Cancel</button><button className="button primary" disabled={mutation.isPending} type="submit"><Save size={16} /> {mutation.isPending ? 'Saving…' : 'Save changes'}</button></div>
  </form>;
}
